/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Button, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { Contributor } from 'bundles/discussions/lib/types';
import store from 'bundles/discussions/utils/DiscussionsV2ContextStorage';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/index';

import formatCount from '../../utils/formatCount';

type Props = {
  creator?: Contributor;
  upvotes?: number;
  isUpvoted?: boolean;
  upvoteError?: boolean;
  ariaDescribedBy?: string;
  forumPostId: string;
  theme: Theme;
  title?: string;
};

type State = {
  clickedLike: boolean;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

class LikeButton extends React.Component<Props, State> {
  static contextTypes = {
    like: PropTypes.func.isRequired,
    unlike: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof LikeButton.contextTypes>;

  state = {
    clickedLike: false,
  };

  componentDidMount() {
    this.setState({ clickedLike: this.props.isUpvoted || false });
  }

  toggleLike = () => {
    const { clickedLike } = this.state;

    if (clickedLike) {
      this.context.unlike();
      this.setState({ clickedLike: false });
    } else {
      this.context.like();
      this.setState({ clickedLike: true });
    }
  };

  render() {
    if (!this.props.creator) return null;
    const { forumPostId, creator, upvoteError, ariaDescribedBy, title } = this.props;
    const storeContextData = store.get(forumPostId) || {};
    const { userId } = storeContextData;
    const postedByCurrentUser = creator?.learnerId?.toString() === userId;
    const hasError = upvoteError;
    const message =
      this.props.upvotes && this.props.upvotes > 0
        ? _t('Like #{likeCount}', { likeCount: formatCount(this.props.upvotes) })
        : _t('Like');

    let likeIcon;
    const liked = this.state.clickedLike;

    if (postedByCurrentUser) {
      likeIcon = <ThumbsUpIcon size="small" color="support" />;
    } else if (this.state.clickedLike) {
      likeIcon = <ThumbsUpFilledIcon size="small" color="interactive" />;
    } else {
      likeIcon = <ThumbsUpIcon size="small" color="interactive" />;
    }

    return (
      <TrackedButton
        className="rc-forumsV2_LikeButton"
        trackingName="discussion_forum_like_button"
        // Because the tracking action is executed before state update, we have to invert the state in the following line
        trackingData={{ forumPostId, isLikeAction: !this.state.clickedLike }}
        onClick={this.toggleLike}
        aria-label={_t('#{title} #{message}', {
          title,
          message,
        })}
        aria-pressed={liked}
        aria-describedby={ariaDescribedBy}
        size="small"
        variant="ghost"
        icon={likeIcon}
        iconPosition="before"
        disabled={postedByCurrentUser}
      >
        {(hasError && _t('Sorry, something went wrong')) || message}
      </TrackedButton>
    );
  }
}

const LikeButtonWithTheme = (props: $TSFixMe) => {
  const theme = useTheme();
  return <LikeButton {...props} theme={theme} />;
};

export default LikeButtonWithTheme;
